import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageSquareText, MessageCircle, ShieldCheck, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtErr = (err) => { const d = err?.response?.data?.detail; return typeof d === "string" ? d : err?.message || "Errore"; };

// Verifica OTP prima del rilascio card: scelta canale → invio codice 6 cifre → verifica. onVerified(otp_id) / onVerified(null) se cambia il telefono.
export default function OtpVerify({ phone, email, name, verifiedId, onVerified, light = false, testPrefix = "otp" }) {
  const [channel, setChannel] = useState(email ? "email" : "sms");
  const [session, setSession] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);
  const phoneOk = (phone || "").replace(/\D/g, "").length >= 9;

  useEffect(() => { setSession(null); setCode(""); if (verifiedId) onVerified(null); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);
  useEffect(() => {
    if (!session) return;
    setLeft(session.expires_in_sec);
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [session]);

  const request = async () => {
    if (!phoneOk) return toast.error("Inserisci prima un numero di telefono valido");
    if (channel === "email" && !email) return toast.error("Inserisci l'email del cliente o scegli SMS/WhatsApp");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/otp/issue/request`, { phone, channel, email: email || null, name: name || null });
      setSession(data); setCode("");
      toast.success(`Codice inviato via ${channel === "email" ? "email" : channel === "sms" ? "SMS" : "WhatsApp"} a ${data.sent_to_masked}`);
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/otp/issue/verify`, { otp_id: session.otp_id, code });
      onVerified(data.otp_id); toast.success("Identità verificata ✔");
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const box = light ? "rounded-2xl border border-blue-100 bg-blue-50/60 p-4 space-y-3" : "rounded-xl bg-sky-500/5 border border-sky-500/40 p-4 space-y-3";
  const chip = (active) => light
    ? `flex-1 h-10 rounded-xl text-sm font-semibold border flex items-center justify-center gap-1.5 ${active ? "bg-[color:var(--client-brand,#1E40AF)] text-white border-transparent" : "bg-white text-zinc-700 border-zinc-200"} disabled:opacity-40`
    : `flex-1 h-10 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1.5 ${active ? "bg-[color:var(--paprika)] text-white border-transparent" : "bg-zinc-950 text-zinc-300 border-zinc-800"} disabled:opacity-40`;
  const inputCls = light ? "h-12 w-full rounded-xl border border-zinc-200 bg-white text-center font-mono text-2xl tracking-[0.4em] text-zinc-900" : "h-12 w-full rounded-lg border border-zinc-800 bg-zinc-950 text-center font-mono text-2xl tracking-[0.4em]";

  if (verifiedId) {
    return (
      <div className={`${box} flex items-center gap-2 ${light ? "text-emerald-700" : "text-emerald-300"}`} data-testid={`${testPrefix}-verified`}>
        <ShieldCheck className="h-5 w-5" /> <span className="font-semibold">Identità verificata</span> <span className="text-xs opacity-80">— codice OTP confermato</span>
      </div>
    );
  }
  return (
    <div className={box} data-testid={`${testPrefix}-block`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 ${light ? "text-[color:var(--client-brand,#1E40AF)]" : "text-sky-300"}`}>
        <ShieldCheck className="h-3.5 w-3.5" /> Verifica identità (codice OTP)
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={!email} onClick={() => setChannel("email")} className={chip(channel === "email")} data-testid={`${testPrefix}-channel-email`} title={email ? "" : "Inserisci l'email per usare questo canale"}><Mail className="h-4 w-4" /> Email</button>
        <button type="button" onClick={() => setChannel("sms")} className={chip(channel === "sms")} data-testid={`${testPrefix}-channel-sms`}><MessageSquareText className="h-4 w-4" /> SMS</button>
        <button type="button" onClick={() => setChannel("whatsapp")} className={chip(channel === "whatsapp")} data-testid={`${testPrefix}-channel-whatsapp`}><MessageCircle className="h-4 w-4" /> WhatsApp</button>
      </div>
      {!session ? (
        <button type="button" disabled={busy || !phoneOk} onClick={request} data-testid={`${testPrefix}-send`}
          className={`w-full h-11 rounded-xl font-semibold text-white disabled:opacity-40 ${light ? "bg-[color:var(--client-brand,#1E40AF)]" : "bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"}`}>
          {busy ? "Invio…" : "Invia codice di verifica"}
        </button>
      ) : (
        <div className="space-y-2">
          <div className={`text-xs ${light ? "text-zinc-600" : "text-zinc-400"}`} data-testid={`${testPrefix}-sent-info`}>
            Codice inviato a <b>{session.sent_to_masked}</b> · valido ancora <b>{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</b>
          </div>
          <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="••••••" className={inputCls} data-testid={`${testPrefix}-code`} />
          <div className="flex gap-2">
            <button type="button" disabled={busy || code.length !== 6} onClick={verify} data-testid={`${testPrefix}-verify`}
              className={`flex-1 h-11 rounded-xl font-semibold text-white disabled:opacity-40 ${light ? "bg-[color:var(--client-brand,#1E40AF)]" : "bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"}`}>
              {busy ? "Verifica…" : "Verifica codice"}
            </button>
            <button type="button" disabled={busy || left > 0} onClick={request} data-testid={`${testPrefix}-resend`}
              className={`h-11 px-3 rounded-xl text-sm font-semibold border disabled:opacity-40 ${light ? "border-zinc-200 text-zinc-700 bg-white" : "border-zinc-800 text-zinc-300"}`} title="Reinvia (disponibile alla scadenza)">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
